import { ValidatorFn, ValidationErrors, AbstractControl, FormGroup, FormArray, FormControl } from '@angular/forms';
import { TransformationProtocol } from '../../models/transformationProtocol';


export const matchingPasswords: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const passwordControl = control.get('password');
  const confirmControl = control.get('confirmPassword');

  if (!passwordControl || !confirmControl) {
    return null;
  }

  return passwordControl.value === confirmControl.value
    ? null
    : { mismatchedPasswords: true };
};

export const noWhitespaceValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (!control.value) {
    return null;
  }
  return control.value.trim().length ? null : { whitespace: true };
};

export const uniqueFileDefinitionFieldDefinitions: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const fieldFormArray = control.get('record.fields') as FormArray | null;

  if (!fieldFormArray || fieldFormArray.length === 0) {
    return null;
  }

  // Clear any duplicate-field errors from a previous run before recomputing.
  fieldFormArray.controls.forEach(field => {
    const indexControl = field.get('index');
    if (indexControl?.hasError('duplicateFieldDefinition')) {
      const errors = { ...indexControl.errors };
      delete errors['duplicateFieldDefinition'];
      indexControl.setErrors(Object.keys(errors).length ? errors : null);
    }
  });

  const seen: AbstractControl[] = [];

  fieldFormArray.controls.forEach(field => {
    const indexControl = field.get('index');
    const index = indexControl?.value;
    if (index === undefined || index === null) {
      return;
    }

    const duplicate = seen.find(f => f.get('index')?.value === index);
    if (!duplicate) {
      seen.push(field);
      return;
    }

    indexControl?.setErrors({ ...indexControl?.errors, duplicateFieldDefinition: true });
    duplicate.get('index')?.setErrors({ ...duplicate.get('index')?.errors, duplicateFieldDefinition: true });
  });

  return null;
};

function uniqueNameValidator(existingNames: string[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const name = (control.value as string)?.toLowerCase().trim();
    if (!name || existingNames.length === 0) {
      return null;
    }
    const isDuplicate = existingNames.some(n => n.toLowerCase().trim() === name);
    return isDuplicate ? { duplicateName: true } : null;
  };
}

export function uniqueTransformationProtocolFieldName(tpArray: TransformationProtocol[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const outputFieldIndex = control.get('outputFieldIndex')?.value;
    const outputFieldName = (control.get('outputFieldName')?.value as string)?.toLowerCase().trim();

    if (!outputFieldName || tpArray.length === 0) {
      return null;
    }

    const isDuplicate = tpArray.some(
      tp => tp.outputFieldIndex !== outputFieldIndex && tp.outputFieldName.toLowerCase().trim() === outputFieldName
    );

    return isDuplicate ? { duplicateName: true } : null;
  };
}

export function uniqueMetadataName(nameArray: string[]): ValidatorFn {
  return uniqueNameValidator(nameArray);
}

export function uniquePresetName(nameArray: string[]): ValidatorFn {
  return uniqueNameValidator(nameArray);
}

export interface BooleanFn {
    (): boolean;
}

export function conditionalValidator(predicate: BooleanFn, validator: ValidatorFn, errorNamespace?: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.parent) {
      return null;
    }

    let error: ValidationErrors | null = null;
    if (predicate()) {
      error = validator(control);
    }

    return errorNamespace && error ? { [errorNamespace]: error } : error;
  };
}

export class CustomEmailValidator {
  private static readonly emailRegExp =
    /^(?=.{1,254}$)(?=.{1,64}@)[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+(\.[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+)*@[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*$/;

  private static isValid(email: string): boolean {
    return CustomEmailValidator.emailRegExp.test(email.toLowerCase());
  }

  public static isMultiValid: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as string;
    if (!value?.length) {
      return null;
    }

    const emails = value
      .split(',')
      .map(email => email.trim())
      .filter(email => email.length > 0);

    if (emails.length === 0) {
      return { invalidEmail: true };
    }

    const allValid = emails.every(email => CustomEmailValidator.isValid(email));
    return allValid ? null : { invalidEmail: true };
  };
}

export function isValidDate(d: unknown): d is Date {
    return d instanceof Date && !isNaN(d.getTime());
}